import { useState } from 'react';

import RecipientModal from '../RecipientModal/RecipientModal';
import Button from '@shared/components/atoms/Button';
import DateInput from '@shared/components/atoms/DateInput/DateInput';

import styles from './DocumentInfoForm.module.scss';

/**
 * DocumentInfoForm 컴포넌트
 * - 문서 정보 입력 폼 (보존년한, 문서번호, 기안일자, 기안부서, 기안자, 참조자, 시행자, 제목)
 * - Figma 디자인 기반 테이블 레이아웃
 * - 편집 모드와 미리보기 모드 지원
 */

function DocumentInfoForm({
  documentInfo,
  onDocumentInfoChange,
  isPreview = false,
  isEditable = false,
  isDraftDateEditable = false,
  section = 'all',
  onSubjectChange,
}) {
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [executorModalOpen, setExecutorModalOpen] = useState(false);

  // 참조자/시행자 표시 문자열 (마우스 오버 시 툴팁으로 전체 목록 표시)
  const recipientDisplayText =
    documentInfo.recipients?.length > 0
      ? documentInfo.recipients.map((r) => (typeof r === 'string' ? r : r.name)).join(', ')
      : '';
  const executorDisplayText =
    documentInfo.executors?.length > 0
      ? documentInfo.executors.map((e) => (typeof e === 'string' ? e : e.name)).join(', ')
      : '';

  // section: 'top' (보존년한~기안자), 'bottom' (참조자~제목), 'all' (전체)
  // 상단 섹션 (Figma 디자인 순서: 보존년한, 문서번호, 기안일자, 기안부서, 기안자)
  // Figma: 270px 너비, 132px 높이 (5행 x 26px + 상단 2px 라인)
  const topSection = (
    <>
      {/* 테이블 상단 파란색 라인 - 피그마: table header row */}
      <div className={styles.tableTopLine} data-name="table header row" />
      <div className={styles.infoRow}>
        <label className={styles.label}>보존년한</label>
        <div className={styles.valueCell}>
          <span className={styles.value}>{documentInfo.retentionPeriod}</span>
        </div>
      </div>
      <div className={styles.infoRow}>
        <label className={styles.label}>문서번호</label>
        <div className={styles.valueCell}>
          <span className={styles.value}>{documentInfo.documentNumber || '-'}</span>
        </div>
      </div>
      <div className={styles.infoRow}>
        <label className={styles.label}>기안일자</label>
        <div className={styles.valueCell}>
          {isDraftDateEditable ? (
            <DateInput
              value={documentInfo.draftDate || ''}
              onChange={(newValue) => {
                if (onDocumentInfoChange) {
                  onDocumentInfoChange((prev) => ({
                    ...prev,
                    draftDate: newValue,
                  }));
                }
              }}
              maxDate={new Date()}
              placeholder="YYYY-MM-DD"
              className={styles.dateInputCustom}
            />
          ) : (
            <span className={styles.value}>{documentInfo.draftDate}</span>
          )}
        </div>
      </div>
      <div className={styles.infoRow}>
        <label className={styles.label}>기안부서</label>
        <div className={styles.valueCell}>
          <span className={styles.value}>{documentInfo.draftingDepartment || '-'}</span>
        </div>
      </div>
      <div className={styles.infoRow}>
        <label className={styles.label}>기안자</label>
        <div className={styles.valueCell}>
          <span className={styles.value}>{documentInfo.drafter || '-'}</span>
        </div>
      </div>
    </>
  );

  // 하단 섹션 (참조자, 시행자, 제목) - Figma 디자인 기반
  // Figma: Frame 427321511 - 참조자/시행자가 가로로 나란히 배치 (layout_Z75U0T: mode: row)
  // 그 아래 구분선, 그 아래 제목 행
  const bottomSection = (
    <>
      {/* 테이블 상단 파란색 라인 - Figma: fill_CX5V3I (#B2B5CC), 2px, table header row */}
      <div className={styles.tableTopLine} data-name="table header row" />
      {/* 참조자/시행자 영역 - Figma: layout_Z75U0T (row, stretch) */}
      <div className={styles.recipientContainer}>
        {/* 참조자 행 - Figma: Frame 427320738, 높이 33px */}
        <div className={styles.recipientRow}>
          <label className={styles.recipientLabel}>참조자</label>
          <div className={styles.recipientValue}>
            {isPreview && !isEditable ? (
              // 결재완료가 아닐 때: 버튼은 보이지만 비활성화
              <div className={styles.inputGroup}>
                <Button
                  variant="default"
                  size="small"
                  className={styles.selectButton}
                  onClick={() => setRecipientModalOpen(true)}
                  disabled={true}
                >
                  선택
                </Button>
                <input
                  type="text"
                  className={`${styles.input} ${styles.disabled}`}
                  value={recipientDisplayText}
                  title={recipientDisplayText}
                  readOnly
                  disabled={true}
                  placeholder=""
                />
              </div>
            ) : (
              <div className={styles.inputGroup}>
                <Button
                  variant="default"
                  size="small"
                  className={styles.selectButton}
                  onClick={() => {
                    setRecipientModalOpen(true);
                  }}
                  disabled={isPreview && !isEditable}
                >
                  선택
                </Button>
                <input
                  type="text"
                  className={`${styles.input} ${isPreview && !isEditable ? styles.disabled : ''}`}
                  value={recipientDisplayText}
                  title={recipientDisplayText}
                  readOnly
                  disabled={isPreview && !isEditable}
                  placeholder=""
                />
              </div>
            )}
          </div>
        </div>
        {/* 시행자 행 - Figma: Frame 427321493, 높이 33px */}
        <div className={styles.recipientRow}>
          <label className={styles.recipientLabel}>시행자</label>
          <div className={styles.recipientValue}>
            {isPreview && !isEditable ? (
              // 결재완료가 아닐 때: 버튼은 보이지만 비활성화
              <div className={styles.inputGroup}>
                <Button
                  variant="default"
                  size="small"
                  className={styles.selectButton}
                  onClick={() => setExecutorModalOpen(true)}
                  disabled={true}
                >
                  선택
                </Button>
                <input
                  type="text"
                  className={`${styles.input} ${styles.disabled}`}
                  value={executorDisplayText}
                  title={executorDisplayText}
                  readOnly
                  disabled={true}
                  placeholder=""
                />
              </div>
            ) : (
              <div className={styles.inputGroup}>
                <Button
                  variant="default"
                  size="small"
                  className={styles.selectButton}
                  onClick={() => {
                    setExecutorModalOpen(true);
                  }}
                  disabled={isPreview && !isEditable}
                >
                  선택
                </Button>
                <input
                  type="text"
                  className={`${styles.input} ${isPreview && !isEditable ? styles.disabled : ''}`}
                  value={executorDisplayText}
                  title={executorDisplayText}
                  readOnly
                  disabled={isPreview && !isEditable}
                  placeholder=""
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {/* 중간 구분선 - Figma: fill_98F71L (#E1E1E1), 1px */}
      <div className={styles.tableDividerLine} />
      {/* 제목 영역 - Figma: Frame 427321494 (layout_V8UU9D) */}
      <div className={styles.subjectRow}>
        <label className={styles.subjectLabel}>제목</label>
        <div className={styles.subjectValue}>
          {isPreview ? (
            <span className={styles.subjectText}>{documentInfo.subject || '-'}</span>
          ) : (
            <input
              type="text"
              className={styles.subjectInput}
              value={documentInfo.subject}
              maxLength={200}
              onChange={function (e) {
                const newValue = e.target.value;
                // 200자 제한
                if (newValue.length > 200) {
                  // 200자 초과 시 알럿 표시 (부모 컴포넌트에서 처리)
                  if (onSubjectChange) {
                    onSubjectChange(newValue.substring(0, 200), true);
                  } else {
                    // onSubjectChange가 없으면 기본 동작
                    onDocumentInfoChange(function (prev) {
                      return { ...prev, subject: newValue.substring(0, 200) };
                    });
                  }
                } else {
                  // 200자 이하일 때는 정상 처리
                  if (onSubjectChange) {
                    onSubjectChange(newValue, false);
                  } else {
                    onDocumentInfoChange(function (prev) {
                      return { ...prev, subject: newValue };
                    });
                  }
                }
              }}
              placeholder="제목을 입력하세요"
            />
          )}
        </div>
      </div>
    </>
  );

  // isPreview이고 isEditable이 false일 때만 early return (모달 없이)
  // isPreview이고 isEditable이 true일 때는 모달도 함께 렌더링해야 함
  if (isPreview && !isEditable) {
    return (
      <div
        className={`${styles.documentInfo} ${section === 'top' ? styles.topSection : ''} ${section === 'bottom' ? styles.bottomSection : ''}`}
      >
        {section === 'all' || section === 'top' ? topSection : null}
        {section === 'all' || section === 'bottom' ? bottomSection : null}
      </div>
    );
  }

  // 수신자 선택 핸들러
  function handleRecipientSelect(selectedList) {
    if (onDocumentInfoChange) {
      onDocumentInfoChange(function (prev) {
        return {
          ...prev,
          recipients: selectedList,
        };
      });
    } else {
      console.warn('[DocumentInfoForm] onDocumentInfoChange가 없습니다!');
    }
    setRecipientModalOpen(false);
  }

  // 시행자 선택 핸들러
  function handleExecutorSelect(selectedList) {
    if (onDocumentInfoChange) {
      onDocumentInfoChange(function (prev) {
        return {
          ...prev,
          executors: selectedList,
        };
      });
    } else {
      console.warn('[DocumentInfoForm] onDocumentInfoChange가 없습니다!');
    }
    setExecutorModalOpen(false);
  }

  // 수신자 목록 변환 (기존 문자열 배열 호환)
  // rank, position, team 정보가 포함되도록 보장
  const recipientList =
    documentInfo.recipients && documentInfo.recipients.length > 0
      ? documentInfo.recipients.map((r) => {
          if (typeof r === 'string') {
            return { name: r, enumber: 0, position: '', team: '' };
          }
          // 객체인 경우 rank, position, team 정보가 없으면 빈 문자열로 설정
          return {
            enumber: r.enumber || 0,
            name: r.name || '',
            position: r.position || '',
            team: r.team || '',
          };
        })
      : [];

  // 시행자 목록 변환 (기존 문자열 배열 호환)
  // rank, position, team 정보가 포함되도록 보장
  const executorList =
    documentInfo.executors && documentInfo.executors.length > 0
      ? documentInfo.executors.map((e) => {
          if (typeof e === 'string') {
            return { name: e, enumber: 0, position: '', team: '' };
          }
          // 객체인 경우 rank, position, team 정보가 없으면 빈 문자열로 설정
          return {
            enumber: e.enumber || 0,
            name: e.name || '',
            position: e.position || '',
            team: e.team || '',
          };
        })
      : [];

  return (
    <>
      <div
        className={`${styles.documentInfo} ${section === 'top' ? styles.topSection : ''} ${section === 'bottom' ? styles.bottomSection : ''}`}
      >
        {section === 'all' || section === 'top' ? topSection : null}
        {section === 'all' || section === 'bottom' ? bottomSection : null}
      </div>

      {(!isPreview || isEditable) && (
        <>
          <RecipientModal
            isOpen={recipientModalOpen}
            onClose={() => {
              setRecipientModalOpen(false);
            }}
            onSelect={handleRecipientSelect}
            title="참조자 선택"
            selectedRecipients={recipientList}
          />
          <RecipientModal
            isOpen={executorModalOpen}
            onClose={() => {
              setExecutorModalOpen(false);
            }}
            onSelect={handleExecutorSelect}
            title="시행자 선택"
            selectedRecipients={executorList}
          />
        </>
      )}
    </>
  );
}

export default DocumentInfoForm;
